import { useEffect, useState } from "react";
import { Download, Smartphone, X, Share, Plus, Apple, Smartphone as Android } from "lucide-react";

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

export default function InstallApp() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // già installata
    if (localStorage.getItem("fabri_install_dismissed")) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    // iOS non emette beforeinstallprompt → mostra banner manuale dopo 3s
    if (isIOS()) setTimeout(() => setShowBanner(true), 3000);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setShowBanner(false);
        localStorage.setItem("fabri_install_dismissed", "1");
      }
      setDeferredPrompt(null);
    } else if (isIOS()) {
      setShowIOSHelp(true);
    }
  };

  const dismiss = () => {
    setShowBanner(false);
    localStorage.setItem("fabri_install_dismissed", "1");
  };

  if (!showBanner) return null;

  return (
    <>
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-40 glass-strong rounded-2xl p-4 border border-[#ff0033]/40 shadow-[0_0_30px_rgba(255,0,51,0.3)]"
        data-testid="install-banner">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#ff0033]/15 grid place-items-center shrink-0">
            <Smartphone size={20} className="text-[#ff0033]" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-sm">Installa FabriRent</div>
            <div className="text-[11px] text-white/55 mt-0.5 leading-relaxed">
              Aggiungi l'app alla home del tuo telefono e usala come una app nativa, anche offline.
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={install} data-testid="install-app-btn"
                className="btn-neon px-4 py-1.5 rounded-full text-xs inline-flex items-center gap-1.5">
                <Download size={12} /> Installa
              </button>
              <button onClick={dismiss} className="px-3 py-1.5 rounded-full text-xs text-white/60 hover:text-white">
                Più tardi
              </button>
            </div>
          </div>
          <button onClick={dismiss} className="text-white/40 hover:text-white"><X size={16} /></button>
        </div>
      </div>

      {/* iOS step-by-step modal */}
      {showIOSHelp && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setShowIOSHelp(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md glass-strong rounded-3xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Apple size={24} className="text-white" />
              <div className="font-display text-xl font-bold">Installa su iPhone/iPad</div>
            </div>
            <ol className="space-y-3 text-sm">
              <li className="flex gap-3">
                <span className="w-7 h-7 rounded-full bg-[#ff0033]/20 border border-[#ff0033]/40 grid place-items-center text-xs font-bold shrink-0">1</span>
                <span>Apri questa pagina in <b>Safari</b> (non Chrome / non FB)</span>
              </li>
              <li className="flex gap-3">
                <span className="w-7 h-7 rounded-full bg-[#ff0033]/20 border border-[#ff0033]/40 grid place-items-center text-xs font-bold shrink-0">2</span>
                <span>Tocca il pulsante <Share size={14} className="inline mx-1" /> <b>Condividi</b> in basso</span>
              </li>
              <li className="flex gap-3">
                <span className="w-7 h-7 rounded-full bg-[#ff0033]/20 border border-[#ff0033]/40 grid place-items-center text-xs font-bold shrink-0">3</span>
                <span>Scegli <Plus size={14} className="inline mx-1" /> <b>Aggiungi alla schermata Home</b></span>
              </li>
              <li className="flex gap-3">
                <span className="w-7 h-7 rounded-full bg-[#ff0033]/20 border border-[#ff0033]/40 grid place-items-center text-xs font-bold shrink-0">4</span>
                <span>Conferma con <b>Aggiungi</b>. FabriRent ora è sulla home come una app vera.</span>
              </li>
            </ol>
            <button onClick={() => setShowIOSHelp(false)} className="mt-5 w-full py-2.5 rounded-full bg-white/[0.05] border border-white/10 text-sm">Capito</button>
          </div>
        </div>
      )}
    </>
  );
}
